import { TooltipProvider } from '@heidi/design-system/tooltip';
import { cn } from '@/lib/cn';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TimelineEntry } from '../types/types';
import { ChatQuestionNavLine } from './ChatQuestionNavLine';
import { extractUserMessages, MAX_LINE_LENGTH } from './chatQuestionNavUtils';

interface ChatQuestionNavProps {
  /** The conversation timeline containing all messages */
  timeline: TimelineEntry[];
  /** Ref to the scroll area's viewport element */
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  /** Whether a message is currently streaming */
  isStreaming?: boolean;
  /** Optional className for styling */
  className?: string;
}

/** Minimum number of messages to show the navigation */
const MIN_MESSAGES_TO_SHOW = 2;

/** Delay before hiding the nav after mouse leaves or scroll stops (ms) */
const HIDE_DELAY_MS = 800;

/**
 * ChatQuestionNav - Grok-style lightweight navigation for chat messages.
 * Each exchange (user question + AI response) is represented by a single line.
 */
export const ChatQuestionNav = ({
  timeline,
  scrollAreaRef,
  isStreaming = false,
  className,
}: ChatQuestionNavProps): JSX.Element | null => {
  const navRef = useRef<HTMLDivElement>(null);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = null;
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), HIDE_DELAY_MS);
  }, [clearHideTimeout]);

  const showNav = useCallback(() => {
    clearHideTimeout();
    setIsVisible(true);
  }, [clearHideTimeout]);

  const handleMouseEnter = useCallback(() => showNav(), [showNav]);
  const handleMouseLeave = useCallback(() => scheduleHide(), [scheduleHide]);

  useEffect(() => () => clearHideTimeout(), [clearHideTimeout]);

  // Extract user messages from timeline
  const userMessages = useMemo(() => extractUserMessages(timeline), [timeline]);

  // Get the viewport element from the scroll area
  const getViewport = useCallback(() => {
    return scrollAreaRef.current?.querySelector(
      '[data-radix-scroll-area-viewport]'
    ) as HTMLElement | null;
  }, [scrollAreaRef]);

  const calculateActiveIndex = useCallback(
    (viewport: HTMLElement): number | null => {
      if (userMessages.length === 0) return null;

      const viewportRect = viewport.getBoundingClientRect();
      const targetTop = viewportRect.top + 60;

      let bestIndex = 0;
      let bestDistance = Infinity;

      userMessages.forEach((message, index) => {
        const userElement = viewport.querySelector<HTMLElement>(
          `[data-message-id="${message.id}"]`
        );
        if (!userElement) return;

        const userRect = userElement.getBoundingClientRect();
        const distance = Math.abs(userRect.top - targetTop);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      return bestIndex;
    },
    [userMessages]
  );

  // Scroll to user message - positions it at top with small padding
  const scrollToMessage = useCallback(
    (messageId: string, index: number) => {
      const viewport = getViewport();
      if (!viewport) return;

      const messageElement = viewport.querySelector<HTMLElement>(
        `[data-message-id="${messageId}"]`
      );

      if (messageElement) {
        const originalMargin = messageElement.style.scrollMarginTop;
        messageElement.style.scrollMarginTop = '16px';

        messageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        setActiveIndex(index);

        setTimeout(() => {
          messageElement.style.scrollMarginTop = originalMargin;
        }, 500);
      }
    },
    [getViewport]
  );

  // Track scroll position to highlight the line closest to viewport TOP,
  // and show/hide nav on scroll activity.
  useEffect(() => {
    const viewport = getViewport();
    if (!viewport || userMessages.length === 0) return;

    const handleScroll = () => {
      showNav();
      scheduleHide();
      setActiveIndex(calculateActiveIndex(viewport));
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    // Set initial active line without forcing nav visible.
    setActiveIndex(calculateActiveIndex(viewport));

    return () => viewport.removeEventListener('scroll', handleScroll);
  }, [calculateActiveIndex, getViewport, scheduleHide, showNav, userMessages.length]);

  // Don't render if there aren't enough messages
  if (userMessages.length < MIN_MESSAGES_TO_SHOW) {
    return null;
  }

  return (
    <nav
      ref={navRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        // Position: right-3 (12px) - small gap from scrollbar
        'absolute right-3 top-1/2 z-50 -translate-y-1/2',
        'group/nav',
        // Fade transition matching scrollbar behavior
        'transition-opacity duration-200 ease-out',
        isVisible ? 'opacity-100' : 'opacity-0',
        // Only show on large screens (≥1024px) with hover capability
        // Hides on: mobile, tablets, touch-only devices
        'hidden [@media(hover:hover)]:min-[960px]:flex',
        className
      )}
      aria-label="Message navigation"
    >
      <TooltipProvider delayDuration={0} skipDelayDuration={0}>
        <div
          className="flex flex-col items-end gap-0"
          style={{ width: `${MAX_LINE_LENGTH}px` }}
        >
          {userMessages.map((message, index) => (
            <ChatQuestionNavLine
              key={message.id}
              messageId={message.id}
              messageContent={message.content}
              index={index}
              isActive={activeIndex === index}
              onScrollToMessage={() => scrollToMessage(message.id, index)}
            />
          ))}
        </div>
      </TooltipProvider>
    </nav>
  );
};

ChatQuestionNav.displayName = 'ChatQuestionNav';
